import PropTypes from "prop-types";
import { useEffect, useMemo, useState, useContext } from "react";
import { gql, useMutation } from "@apollo/client";
import { Card, CircularProgress, Modal } from "@mui/material";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInputNumber from "components/SoftInputNumberUpgrade";
import { MessageManager } from "context";

const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    maxHeight: "90%",
    width: { xs: "92%", sm: "80%", md: "70%", xxl: "55%" },
    overflowY: "auto",
    p: 3,
};

const UPDATE_TAREA_ITEM = gql`
    mutation updateTareaItem(
        $idItem: ID!
        $cantidadInicial: Float
        $cantidadFinal: Float
        $pedidoCompras: Float
    ) {
        updateTareaItem(
            idItem: $idItem
            cantidadInicial: $cantidadInicial
            cantidadFinal: $cantidadFinal
            pedidoCompras: $pedidoCompras
        ) {
            id
            estado
            items {
                id
                texto
                tipoItem
                unidadAbreviatura
                cantidadInicial
                cantidadFinal
                pedidoCompras
            }
        }
    }
`;

const isFilled = (value) => value !== null && value !== undefined && value !== "";

export default function ModalDetalleControlCafeteria({ open, onClose, task, onUpdated }) {
    const { handleSnackbar } = useContext(MessageManager);
    const [localItems, setLocalItems] = useState([]);
    const [saving, setSaving] = useState(false);
    const [updateTareaItem] = useMutation(UPDATE_TAREA_ITEM);

    useEffect(() => {
        if (task?.items) {
            setLocalItems(
                task.items.map((it) => ({
                    id: it.id,
                    texto: it.texto,
                    tipoItem: it.tipoItem,
                    unidadAbreviatura: it.unidadAbreviatura,
                    cantidadInicial: it.cantidadInicial ?? "",
                    cantidadFinal: it.cantidadFinal ?? "",
                    pedidoCompras: it.pedidoCompras ?? "",
                }))
            );
        } else {
            setLocalItems([]);
        }
    }, [task]);

    const allComplete = useMemo(() => {
        if (!localItems.length) return false;
        return localItems.every(
            (it) => isFilled(it.cantidadInicial) && isFilled(it.cantidadFinal) && isFilled(it.pedidoCompras)
        );
    }, [localItems]);

    const handleItemChange = (id, field, value) => {
        setLocalItems((prev) =>
            prev.map((it) => (it.id === id ? { ...it, [field]: value } : it))
        );
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const promises = [];
            localItems.forEach((item) => {
                const original = task?.items?.find((it) => it.id === item.id);
                if (!original) return;

                const changed =
                    (item.cantidadInicial ?? "") !== (original.cantidadInicial ?? "") ||
                    (item.cantidadFinal ?? "") !== (original.cantidadFinal ?? "") ||
                    (item.pedidoCompras ?? "") !== (original.pedidoCompras ?? "");

                if (changed) {
                    promises.push(
                        updateTareaItem({
                            variables: {
                                idItem: item.id,
                                cantidadInicial: item.cantidadInicial === "" ? null : Number(item.cantidadInicial),
                                cantidadFinal: item.cantidadFinal === "" ? null : Number(item.cantidadFinal),
                                pedidoCompras: item.pedidoCompras === "" ? null : Number(item.pedidoCompras),
                            },
                        })
                    );
                }
            });

            if (promises.length === 0 && allComplete && localItems[0]) {
                const first = localItems[0];
                promises.push(
                    updateTareaItem({
                        variables: {
                            idItem: first.id,
                            cantidadInicial: first.cantidadInicial === "" ? null : Number(first.cantidadInicial),
                            cantidadFinal: first.cantidadFinal === "" ? null : Number(first.cantidadFinal),
                            pedidoCompras: first.pedidoCompras === "" ? null : Number(first.pedidoCompras),
                        },
                    })
                );
            }

            if (promises.length > 0) {
                await Promise.all(promises);
                handleSnackbar("Cambios guardados correctamente", "success");
                onUpdated?.();
            } else {
                handleSnackbar("No hubo cambios", "info");
            }

            onClose();
        } catch (e) {
            handleSnackbar(e?.message || "Error al guardar cambios", "error");
        } finally {
            setSaving(false);
        }
    };

    if (!task) return null;

    return (
        <Modal open={open} onClose={onClose}>
            <Card sx={style}>
                <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <SoftTypography variant="h6" fontWeight="bold">
                        Control cafetería
                    </SoftTypography>
                    <SoftTypography variant="caption" color="text">
                        {task.turno === "TARDE" ? "Turno: Tarde" : "Turno: Mañana"}
                    </SoftTypography>
                </SoftBox>

                <SoftBox display="grid" gridTemplateColumns="1fr" gap={1}>
                    <SoftBox display="grid" gridTemplateColumns="1fr 2fr 1fr 1fr 1fr" gap={1}>
                        <SoftTypography variant="caption" fontWeight="bold">Tipo</SoftTypography>
                        <SoftTypography variant="caption" fontWeight="bold">Nombre</SoftTypography>
                        <SoftTypography variant="caption" fontWeight="bold">Cant. inicial</SoftTypography>
                        <SoftTypography variant="caption" fontWeight="bold">Cant. final</SoftTypography>
                        <SoftTypography variant="caption" fontWeight="bold">Pedido compras</SoftTypography>
                    </SoftBox>

                    {localItems.map((item) => (
                        <SoftBox key={item.id} display="grid" gridTemplateColumns="1fr 2fr 1fr 1fr 1fr" gap={1} alignItems="center">
                            <SoftTypography variant="caption">
                                {item.tipoItem === "DESCARTABLE" ? "Descartable" : "Producto"}
                            </SoftTypography>
                            <SoftTypography variant="caption">{item.texto}</SoftTypography>
                            <SoftInputNumber
                                name="cantidadInicial"
                                value={item.cantidadInicial}
                                onChange={(e) => handleItemChange(item.id, "cantidadInicial", e.target.value)}
                                icon={{
                                    component: (
                                        <SoftTypography variant="caption">
                                            {item.unidadAbreviatura || ""}
                                        </SoftTypography>
                                    ),
                                    direction: "right",
                                }}
                            />
                            <SoftInputNumber
                                name="cantidadFinal"
                                value={item.cantidadFinal}
                                onChange={(e) => handleItemChange(item.id, "cantidadFinal", e.target.value)}
                                icon={{
                                    component: (
                                        <SoftTypography variant="caption">
                                            {item.unidadAbreviatura || ""}
                                        </SoftTypography>
                                    ),
                                    direction: "right",
                                }}
                            />
                            <SoftInputNumber
                                name="pedidoCompras"
                                value={item.pedidoCompras}
                                onChange={(e) => handleItemChange(item.id, "pedidoCompras", e.target.value)}
                                icon={{
                                    component: (
                                        <SoftTypography variant="caption">
                                            {item.unidadAbreviatura || ""}
                                        </SoftTypography>
                                    ),
                                    direction: "right",
                                }}
                            />
                        </SoftBox>
                    ))}

                    {!localItems.length && (
                        <SoftTypography variant="caption" color="text">
                            No hay items
                        </SoftTypography>
                    )}
                </SoftBox>

                <SoftBox mt={4} display="flex" justifyContent="flex-end" gap={1}>
                    <SoftButton variant="outlined" color="dark" onClick={onClose} disabled={saving}>
                        Cancelar
                    </SoftButton>
                    <SoftButton
                        variant="outlined"
                        color="success"
                        onClick={handleSave}
                        disabled={saving || !allComplete}
                    >
                        Finalizar tarea
                    </SoftButton>
                    <SoftButton color="primary" onClick={handleSave} disabled={saving}>
                        {saving ? <CircularProgress size={18} color="inherit" /> : "Guardar"}
                    </SoftButton>
                </SoftBox>
            </Card>
        </Modal>
    );
}

ModalDetalleControlCafeteria.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    task: PropTypes.object,
    onUpdated: PropTypes.func,
};
